import * as React from 'react';

import { InventoryCountImportError } from 'root/apps/InventoryCount/errors';
import { StatusOverview } from 'root/apps/InventoryCount/types';
import { getApiError, parseApiErrorLength } from 'root/apps/InventoryCount/utils/api-error-codes';

import { inventoryCountURL } from '../utils/helper-url';
import { replaceMatchFieldString } from '../utils/match-field-value';
import { ErrorMessageContext } from './ErrorMessage';

export interface Return {
  isLoading: boolean;
  data: StatusOverview | undefined;
  getStatusData: () => void;
  UseFileUploader: (file: File, email?: string) => void;
  deleteImportProcess: () => void;
  ImportProcess: () => void;
  ClearImportSuccessMessage: () => void;
  ValidateUploadedFile: (
    identifierFields: string,
    matchFieldIdentifier: string,
    matchFieldQty: string
  ) => void;
}

export const StatusDataContext = React.createContext<Return>({} as Return);

interface Props {
  data?: StatusOverview;
}

const Provider: React.FC<Props> = ({ data: providerData, children }) => {
  const { setError } = React.useContext(ErrorMessageContext);
  const [data, setData] = React.useState<StatusOverview | undefined>(providerData);
  const [isLoading, setIsLoading] = React.useState(false);

  const systemCustomerId = window.merchantos.session.getSystemCustomerID() as string;
  const { id: countId } = window.history.getFilters();

  const getStatusData = async () => {
    setIsLoading(true);
    try {
      const statusResponse = await fetch(inventoryCountURL(systemCustomerId, countId));
      await throwOnResponseError(statusResponse);
      const data = await statusResponse.json();
      setData(data.InventoryCountImport);
    } catch (err) {
      if (err instanceof InventoryCountImportError) {
        setError(err);
      } else {
        setError(getApiError('GENERIC'));
      }
    }
    setIsLoading(false);
  };

  const UseFileUploader = async (uploadedFile: File, email?: string) => {
    setIsLoading(true);
    const emailObj = JSON.stringify({ notificationEmail: email });

    const formData = new FormData();
    formData.append('file', uploadedFile);
    formData.append('data', emailObj);

    try {
      const statusResponse = await fetch(
        `/API/Account/${systemCustomerId}/InventoryCount/${countId}/Import.json`,
        {
          // DO NOT SET CONTENT TYPE IN POST METHOD
          // Browser will auto set that when it is calling the api
          method: 'POST',
          body: formData,
        }
      );
      await throwOnResponseError(statusResponse, getApiError('UPLOAD_FILE').message);
      const data = await statusResponse.json();
      setData(data.InventoryCountImport);
    } catch (err) {
      if (err instanceof InventoryCountImportError) {
        setError(err);
      } else {
        setError(getApiError('UPLOAD_FILE'));
      }
    }

    setIsLoading(false);
  };

  const ValidateUploadedFile = async (
    identifierField: string,
    matchFieldIdentifier: string,
    matchFieldQty: string
  ) => {
    setError(null);
    setIsLoading(true);

    const convertedIdentifierField = replaceMatchFieldString(identifierField);

    try {
      const statusResponse = await fetch(
        `/API/Account/${systemCustomerId}/InventoryCount/${countId}/Import.json`,
        {
          method: 'POST',
          body: JSON.stringify({
            identifierField: convertedIdentifierField,
            matchFieldIdentifier,
            matchFieldQty,
          }),
        }
      );
      await throwOnResponseError(statusResponse, getApiError('MAPPING_COLUMN').message);
      const data = await statusResponse.json();
      setData(data.InventoryCountImport);
    } catch (err) {
      if (err instanceof InventoryCountImportError) {
        setError(err);
      } else {
        getApiError('MAPPING_COLUMN');
      }
    }

    setIsLoading(false);
  };

  const ImportProcess = async () => {
    setError(null);
    setIsLoading(true);

    try {
      const statusResponse = await fetch(
        `/API/Account/${systemCustomerId}/InventoryCount/${countId}/Import.json`,
        {
          method: 'POST',
        }
      );
      await throwOnResponseError(statusResponse, getApiError('IMPORT_ERROR').message);
      const data = await statusResponse.json();
      setData(data.InventoryCountImport);
    } catch (err) {
      if (err instanceof InventoryCountImportError) {
        setError(err);
      } else {
        setError(getApiError('IMPORT_ERROR'));
      }
    }

    setIsLoading(false);
  };

  const ClearImportSuccessMessage = async () => {
    setError(null);
    setIsLoading(true);

    try {
      const statusResponse = await fetch(
        `/API/Account/${systemCustomerId}/InventoryCount/${countId}/Import.json`,
        {
          method: 'POST',
        }
      );
      await throwOnResponseError(statusResponse);
      const data = await statusResponse.json();
      getStatusData();
      setData(data.InventoryCountImport);
    } catch (err) {
      if (err instanceof InventoryCountImportError) {
        setError(err);
      } else {
        setError(getApiError('GENERIC'));
      }
    }

    setIsLoading(false);
  };

  const deleteImportProcess = async () => {
    setIsLoading(true);
    try {
      const statusResponse = await fetch(
        `/API/Account/${systemCustomerId}/InventoryCount/${countId}/Import.json`,
        {
          method: 'DELETE',
        }
      );
      await throwOnResponseError(statusResponse);
      const data = await statusResponse.json();
      setData(data.InventoryCountImport);
      setError(null);
    } catch (err) {
      if (err instanceof InventoryCountImportError) {
        setError(err);
      } else {
        setError(getApiError('GENERIC'));
      }
    }
    setIsLoading(false);
  };

  /**
   * If the response is not ok, throw an InventoryCountImportError
   * Attempts to parse the response body for an errorMessage
   * Default to a generic error message
   */
  const throwOnResponseError: (
    response: Response,
    defaultMessage?: string
  ) => Promise<void> = async (response, defaultMessage = getApiError('GENERIC').message) => {
    if (!response.ok) {
      try {
        const errorResponse = await response.json();
        if (errorResponse.InventoryCountImport.errorMessage) {
          let message = errorResponse.InventoryCountImport.errorMessage
            .replaceAll('<var>', '')
            .replaceAll('</var>', '');
          message = parseApiErrorLength(message);
          throw new InventoryCountImportError(message);
        }
        // ensure the try block fails and let the catch block default to a generic error message
        throw new Error();
      } catch (err) {
        if (err instanceof InventoryCountImportError) {
          throw err;
        }
        throw new InventoryCountImportError(defaultMessage);
      }
    }
  };

  return (
    <StatusDataContext.Provider
      value={{
        isLoading,
        data,
        getStatusData,
        UseFileUploader,
        deleteImportProcess,
        ValidateUploadedFile,
        ClearImportSuccessMessage,
        ImportProcess,
      }}
    >
      {children}
    </StatusDataContext.Provider>
  );
};

export default Provider;
